// Test mode: a per-tab declaration that "this war is a test", so the war
// archive can keep tester-vs-tester games apart from real ones.
//
// It is URL-driven, not stored: `?test` in the address bar turns it on,
// `?test=0` turns it off. Localhost and non-production Netlify builds
// (deploy previews, branch deploys) are on by default. There is no
// heuristic — two guest tabs on one desk look exactly like two strangers,
// so the tester declares it, and the lobby shows a TEST WAR tag while it
// is on. Either player carrying the flag at create/join time marks the war.

export const TEST_PARAM = 'test'

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '0.0.0.0'])

// Pure: decide test mode from the pieces of the environment that matter.
//   search       window.location.search ("?test", "?test=0", ...)
//   hostname     window.location.hostname
//   buildContext Netlify's CONTEXT baked in at build time
//                ('production' | 'deploy-preview' | 'branch-deploy' | undefined)
export function detectTestMode({ search = '', hostname = '', buildContext } = {}) {
    const params = new URLSearchParams(search)
    if (params.has(TEST_PARAM)) {
        const v = params.get(TEST_PARAM)
        return !(v === '0' || v === 'off' || v === 'false')
    }
    if (LOCAL_HOSTS.has(hostname)) return true
    if (buildContext && buildContext !== 'production') return true
    return false
}

// Is this tab in test mode right now?
export function isTestMode() {
    if (typeof window === 'undefined') return false
    return detectTestMode({
        search: window.location.search,
        hostname: window.location.hostname,
        buildContext: process.env.REACT_APP_CONTEXT,
    })
}

// Would this tab be in test mode with no `?test` param at all? (Localhost,
// deploy previews.) Tells the lobby whether "exit test mode" needs `?test=0`.
export function isTestModeByDefault() {
    if (typeof window === 'undefined') return false
    return detectTestMode({
        search: '',
        hostname: window.location.hostname,
        buildContext: process.env.REACT_APP_CONTEXT,
    })
}

// The URL that flips test mode on/off for this tab. Other query params survive.
export function testModeToggleUrl({ href, on, defaultOn }) {
    const url = new URL(href)
    if (on) {
        // Turning ON: `?test`. An explicit `?test=0` is dropped.
        url.searchParams.delete(TEST_PARAM)
        url.searchParams.append(TEST_PARAM, '')
    } else if (defaultOn) {
        // Turning OFF where the default is on (localhost): needs `?test=0`.
        url.searchParams.set(TEST_PARAM, '0')
    } else {
        url.searchParams.delete(TEST_PARAM)
    }
    // URLSearchParams renders `test=` for an empty value; keep the bare `?test`.
    return url.toString().replace(/([?&])test=(?=&|$)/, '$1test')
}
